import { cn } from '@/lib/utils'
import type { PropsWithChildren, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

/**
 * Tracks whether a bottom-sticky element is pinned, by watching a zero-height
 * sentinel rendered right after it. The sentinel sits below the viewport
 * exactly while the element is held at the bottom edge.
 */
function useIsStuckToBottom<T extends Element>() {
  const sentinelRef = useRef<T>(null)
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight
        setStuck(
          !entry.isIntersecting &&
            entry.boundingClientRect.top >= viewportBottom - 1
        )
      },
      { rootMargin: '0px 0px -1px 0px', threshold: 0 }
    )
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [])

  return { sentinelRef, stuck }
}

/**
 * Form action bar: fixed to the bottom on phones, and on larger screens a card
 * that sticks to the bottom of the viewport until the page end is reached.
 */
export const StickyActionBar: React.FC<
  PropsWithChildren<{
    className?: string
    /** Shown at the start of the bar, e.g. a prompt to save. */
    message?: ReactNode
  }>
> = ({ children, className, message }) => {
  const { sentinelRef, stuck } = useIsStuckToBottom<HTMLDivElement>()

  return (
    <>
      <div
        data-stuck={stuck || undefined}
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-stone-200 bg-white p-3',
          'md:sticky md:inset-x-auto md:mt-4 md:rounded-2xl md:border-t-0 md:p-4 md:shadow-card md:transition-[border-radius]',
          stuck && 'md:rounded-b-none',
          className
        )}
      >
        {message && (
          <p className="mr-auto text-sm font-medium text-stone-700">
            {message}
          </p>
        )}
        {children}
      </div>
      <div ref={sentinelRef} aria-hidden />
    </>
  )
}
